import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toZonedTime } from 'date-fns-tz';
import api from 'state/axios';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { debounce } from 'lodash';
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography,
    TextField, 
    Button, 
    MenuItem, 
    CircularProgress,
    Autocomplete, 
} from '@mui/material';
import { ArrowBackIosNew } from "@mui/icons-material";
import useAuthCheck from 'hooks/useAuthCheck';
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { enGB } from 'date-fns/locale';
import { LAYOUT_HEIGHT } from 'state/constants';
import { parseUTCDate } from 'state/helpers';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import CustomDatePicker from "components/CustomDatePicker";
import TiptapEditor from 'components/TiptapEditor';


const CrewRequest = () => {
    useAuthCheck();
    const navigate = useNavigate();
    const { requestId } = useParams();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [crewScheduleCodes, setCrewScheduleCodes] = useState([]);
    const [crewMembers, setCrewMembers] = useState([]);
    const [request, setRequest] = useState({});

    const editorChangeHandlerRef = useRef(null);

    const initialValues = {
        crewCode: request.crewCode || '',
        crewName: request.crewName || '',
        requestType: request.requestType || '',
        title: request.title || '',
        startDate: request.startDate ? toZonedTime(new Date(request.startDate), 'UTC') : new Date(),
        endDate: request.endDate ? toZonedTime(new Date(request.endDate), 'UTC') : new Date(),
        content: request.content || '',
        status: {
            status: request.status?.status || 'Pending',
            statusDate: request.status?.statusDate || null,
            handler: request.status?.handler || '',
        },
        comments: Array.isArray(request.comments)
            ? request?.comments.map(comment => ({
                comment: comment?.comment || '',
                commentDate: comment?.commentDate || null,
                commentor: comment?.commentor || '',
            }))
            : [],
    };

    const fetchCrewScheduleCodes = useCallback(async () => {
        return await api.get('/crewschedulecodes');
    }, []);

    const fetchCrewMembers = useCallback(async () => {
        return await api.get('/crewmembers');
    }, []);

    const fetchRequest = useCallback(async () => {
        if (requestId) {
            return await api.get(`/crewrequests/${requestId}`);
        }
    }, [requestId]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setError(null);
                setLoading(true);

                const [
                    crewScheduleCodesResponse,
                    crewMembersResponse,
                    requestResponse
                ] = await Promise.all([
                    fetchCrewScheduleCodes(),
                    fetchCrewMembers(),
                    requestId ? fetchRequest() : Promise.resolve({ data: {} }),
                ]);

                const codes = crewScheduleCodesResponse?.data ?? [];
                setCrewScheduleCodes(codes?.filter(item => item.isCrewRequest));
                setCrewMembers(crewMembersResponse?.data || []);
                setRequest(requestResponse?.data?.data || {});
            } catch (error) {
                setError(error);
                toast.error(error?.message || "Failed to fetch data!");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [fetchCrewScheduleCodes, fetchCrewMembers, fetchRequest, requestId]);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const checkoutSchema = yup.object().shape({
        crewCode: yup.string().required('Required'),
        requestType: yup.string().required('Required'),
        title: yup.string().required('Required'),
        startDate: yup
            .date()
            .required('Required')
            .min(today, 'Start date must be today or later'),
        endDate: yup
            .date()
            .required('Required')
            .min(yup.ref('startDate'), 'End date must be >= Start date'),
        content: yup.string().required('Required'),
    });

    const handleFormSubmit = async (values, onSubmitProps) => {
        try {
            const dateFields = ['startDate', 'endDate'];
            const payload = {
                ...values,
                ...dateFields.reduce((acc, field) => {
                    acc[field] = values[field] ? parseUTCDate(values[field]) : null;
                    return acc;
                }, {}),
            };
            const method = requestId ? 'PUT' : 'POST';
            const url = requestId
                ? `/crewrequests/process/${requestId}`
                : '/crewrequests/new';

            const response = await api({ method, url, data: payload });

            if ([200, 201].includes(response.status)) {
                toast.success(requestId ? 'Request updated.' : 'Request created.');
                onSubmitProps.resetForm();
                const search = window.location.search;
                navigate(`/crewrequests${search}`);
            } else {
                toast.error(response.data?.message || 'An unexpected error occurred.');
            }
        } catch (error) {
            setError(error);
            toast.error(error?.message || 'Failed to submit request!');
        }
    };

    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header
                    title="Crew Request"
                    subtitle={`${loading ? 'Loading...' : requestId ? 'Update' : 'Create'}`}
                />
                <Box display="flex" alignItems="center" gap={1}>
                    <StyledButton
                        onClick={() => {
                            const search = window.location.search;
                            navigate(`/crewrequests${search}`);
                        }}
                    >
                        <ArrowBackIosNew />
                    </StyledButton>
                </Box>
            </FlexBetween>

            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <LocalizationProvider 
                        dateAdapter={AdapterDateFns} 
                        adapterLocale={enGB}
                    >
                        <Formik
                            initialValues={initialValues}
                            enableReinitialize
                            validationSchema={checkoutSchema}
                            onSubmit={handleFormSubmit}
                        >
                            {({ 
                                values, 
                                errors, 
                                touched, 
                                handleBlur, 
                                handleChange, 
                                handleSubmit, 
                                setFieldValue 
                            }) => {
                                if (!editorChangeHandlerRef.current) {
                                    editorChangeHandlerRef.current = debounce((newContent) => {
                                        setFieldValue('content', newContent);
                                    }, 500);
                                }

                                const handleEditorChange = (newContent) => {
                                    if (newContent !== values.content) {
                                        editorChangeHandlerRef.current(newContent);
                                    }
                                };

                                return (
                                    <form onSubmit={handleSubmit}>
                                        <Box
                                            display="grid"
                                            gap={2}
                                            gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                            sx={{ '& > div': { gridColumn: isDesktop ? undefined : 'span 12' } }}
                                        >
                                            <Autocomplete
                                                sx={{ gridColumn: "span 6" }}
                                                options={crewMembers}
                                                getOptionLabel={(option) => `${option.crewCode} - ${option.crewName}`}
                                                value={crewMembers?.find((cm) => cm.crewCode === values.crewCode) || null}
                                                onChange={(_, newValue) => {
                                                    if (newValue) {
                                                        setFieldValue("crewCode", newValue.crewCode);
                                                        setFieldValue("crewName", newValue.crewName);
                                                    } else {
                                                        setFieldValue("crewCode", "");
                                                        setFieldValue("crewName", "");
                                                    }
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Crew Member"
                                                        variant="filled"
                                                        required
                                                        error={!!touched.crewCode && !!errors.crewCode}
                                                        helperText={touched.crewCode && errors.crewCode}
                                                    />
                                                )}
                                            />

                                            <TextField
                                                fullWidth
                                                select
                                                required
                                                variant="filled"
                                                label="Request Type"
                                                name="requestType"
                                                value={values.requestType}
                                                onChange={(e) => setFieldValue('requestType', e.target.value)}
                                                onBlur={handleBlur}
                                                error={touched.requestType && Boolean(errors.requestType)}
                                                helperText={touched.requestType && errors.requestType}
                                                sx={{ gridColumn: 'span 6' }}
                                            >
                                                {crewScheduleCodes?.map(item => (
                                                    <MenuItem key={item.code} value={item.code}>
                                                        {item.code + ' - ' + item.description}
                                                    </MenuItem>
                                                ))}
                                            </TextField>

                                            <CustomDatePicker
                                                label="Start Date"
                                                name="startDate"
                                                value={values.startDate}
                                                onChange={setFieldValue}
                                                touched={touched}
                                                errors={errors}
                                                sx={{ gridColumn: "span 6" }}
                                            />

                                            <CustomDatePicker
                                                label="End Date"
                                                name="endDate"
                                                value={values.endDate}
                                                minDate={values.startDate}
                                                onChange={setFieldValue}
                                                touched={touched}
                                                errors={errors}
                                                sx={{ gridColumn: "span 6" }}
                                            />

                                            <TextField
                                                required
                                                fullWidth
                                                variant="filled"
                                                label="Title"
                                                name="title"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={values.title}
                                                error={!!touched.title && !!errors.title}
                                                helperText={touched.title && errors.title}
                                                sx={{ gridColumn: 'span 12' }}
                                            />

                                            <TiptapEditor
                                                aria-label="Content"
                                                content={values.content}
                                                onChange={handleEditorChange}
                                                placeholder="Input content..."
                                                editable
                                                sx={{ gridColumn: 'span 12' }}
                                            />

                                            <Button
                                                fullWidth
                                                type="submit"
                                                variant="contained"
                                                color="secondary"
                                                disabled={loading}
                                                sx={{ gridColumn: 'span 12', height: 42 }}
                                            >
                                                {loading ? <CircularProgress size={24} /> : requestId ? 'Update' : 'Create'}
                                            </Button>
                                        </Box>
                                    </form>
                                );
                            }}
                        </Formik>
                    </LocalizationProvider>
                </Box>
            )}
        </Box>
    );
};

export default CrewRequest;
